/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Activity, Users, Database, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { formatNumber, formatQuota } from '@/lib/format'

interface KpiCardsProps {
  stats: {
    totalCalls: number
    activeUsers: number
    dataStorage: number
    uptime: number
  } | null
  loading: boolean
}

interface KpiItem {
  key: string
  label: string
  value: string
  icon: typeof Activity
  gradient: string
  iconBg: string
}

export function KpiCards({ stats, loading }: KpiCardsProps) {
  const { t } = useTranslation()

  const items: KpiItem[] = [
    {
      key: 'apiCalls',
      label: t('API Calls'),
      value: stats ? formatNumber(stats.totalCalls) : '--',
      icon: Activity,
      gradient: 'from-cyan-500/20 via-cyan-400/10 to-transparent dark:from-cyan-500/15 dark:via-cyan-400/5',
      iconBg: 'bg-cyan-500/15 text-cyan-500 dark:bg-cyan-500/20 dark:text-cyan-400',
    },
    {
      key: 'activeUsers',
      label: t('Active Users'),
      value: stats ? formatNumber(stats.activeUsers) : '--',
      icon: Users,
      gradient: 'from-emerald-500/20 via-emerald-400/10 to-transparent dark:from-emerald-500/15 dark:via-emerald-400/5',
      iconBg: 'bg-emerald-500/15 text-emerald-500 dark:bg-emerald-500/20 dark:text-emerald-400',
    },
    {
      key: 'dataStorage',
      label: t('Data Storage'),
      value: stats ? formatQuota(stats.dataStorage) : '--',
      icon: Database,
      gradient: 'from-violet-500/20 via-violet-400/10 to-transparent dark:from-violet-500/15 dark:via-violet-400/5',
      iconBg: 'bg-violet-500/15 text-violet-500 dark:bg-violet-500/20 dark:text-violet-400',
    },
    {
      key: 'uptime',
      label: t('System Availability'),
      value: stats ? `${formatNumber(stats.uptime)}%` : '--',
      icon: Zap,
      gradient: 'from-amber-500/20 via-amber-400/10 to-transparent dark:from-amber-500/15 dark:via-amber-400/5',
      iconBg: 'bg-amber-500/15 text-amber-500 dark:bg-amber-500/20 dark:text-amber-400',
    },
  ]

  return (
    <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
      {items.map((item) => {
        const Icon = item.icon
        return (
          <div
            key={item.key}
            className={cn(
              'relative overflow-hidden rounded-xl border bg-card/50 p-4 backdrop-blur-sm',
              'bg-linear-to-br',
              item.gradient
            )}
          >
            <div className='flex items-start justify-between gap-3'>
              <div className='flex flex-col gap-2'>
                <span className='text-muted-foreground text-xs font-medium'>
                  {item.label}
                </span>
                {loading ? (
                  <Skeleton className='h-8 w-20' />
                ) : (
                  <span className='text-foreground font-mono text-2xl font-semibold tracking-tight tabular-nums'>
                    {item.value}
                  </span>
                )}
              </div>
              <div
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-lg',
                  item.iconBg
                )}
              >
                <Icon className='size-4.5' aria-hidden='true' />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
